@import 'core';

/**
 * @prop --ads-search-background-color: Background color
 *
 * @prop --ads-search-border-bottom-color: Border color bottom
 * @prop --ads-search-border-color: Border color
 * @prop --ads-search-border-color-disabled: Disabled border color
 * @prop --ads-search-border-color-focus: Focused border color
 * @prop --ads-search-border-color-hover: Hovered border color
 *
 * @prop --ads-search-button-background-color: Button background color
 * @prop --ads-search-button-background-color-focus: Focused button background color
 * @prop --ads-search-button-background-color-hover: Hovered button background color
 *
 * @prop --ads-search-button-color: Button text color
 * @prop --ads-search-button-color-focus: Focused button text color
 * @prop --ads-search-button-color-hover: Hovered button text color
 *
 * @prop --ads-search-height: Height
 * @prop --ads-search-text-size: Text size
 *
 * @prop --ads-search-input-background-color: Input background color
 * @prop --ads-search-input-background-color-disabled: Disabled input background color
 *
 * @prop --ads-search-input-color: Input text color
 * @prop --ads-search-input-color-disabled: Disabled input text color
 * @prop --ads-search-input-color-focus: Focused input text color

 * @prop --ads-search-input-clear-color: Input clear icon color
 * @prop --ads-search-input-clear-color-focus: Focused input clear icon color
 * @prop --ads-search-input-clear-color-hover: Hovered input clear icon color
 *
 * @prop --ads-search-suggestions-background-color: Suggestions background color
 * @prop --ads-search-suggestions-background-color-active: Active suggestions background color
 *
 * @prop --ads-search-suggestions-border-color: Suggestions border color
 *
 * @prop --ads-search-suggestions-color: Suggestions text color
 * @prop --ads-search-suggestions-link-color: Suggestions link color
 */

@mixin font-styles {
  font-family: var(--ads-font-family);
  font-size: var(--ads-search-font-size, $ads-search-font-size);
}

:host {
  display: block;
  height: var(--ads-search-height, $ads-search-height);
  position: relative;
  width: 100%;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
}

:host([theme='default']) {
  @include ads-vars-component-search-default;
}

:host([theme='dark']) {
  @include ads-vars-component-search-dark;
}

:host([dir='ltr']) {
  @include ads-vars-component-search-ltr;
}

:host([dir='rtl']) {
  @include ads-vars-component-search-rtl;
}

.c-search {
  background-color: var(--ads-search-background-color, $ads-search-background-color);
  color: var(--ads-search-input-color, $ads-search-input-color);
  display: flex;
  font-family: var(--ads-font-family);
  line-height: normal;
  margin: 0;
  padding: 0;

  &:not(.is-disabled):focus-within,
  &:not(.is-disabled):hover {
    &.has-button .c-search__button button,
    &.has-button .c-search__button button {
      border-color: var(--ads-search-border-color-focus, $ads-search-border-color-focus);
      color: var(--ads-search-button-color-focus, $ads-search-button-color-focus);
      fill: var(--ads-search-button-color-focus, $ads-search-button-color-focus);
    }

    &.has-button .c-search__input,
    &:not(.has-button) .c-search__input {
      border-color: var(--ads-search-border-color-focus, $ads-search-border-color-focus);
      color: var(--ads-search-input-color-focus, $ads-search-input-color-focus);
    }

    .c-search__icon {
      fill: var(--ads-search-input-color-focus, $ads-search-input-color-focus);
    }

    .c-search__clear {
      fill: var(--ads-search-input-clear-color-focus, $ads-search-input-clear-color-focus);
    }
  }

  &:not(.is-disabled):not(.has-button):focus-within {
    .c-search__icon {
      display: none;
    }

    .c-search__input {
      padding: var(--ads-search-input-padding-focus, $ads-search-input-padding-focus-ltr);
    }
  }

  &.is-disabled {
    cursor: not-allowed;

    &.has-button,
    .c-search__input {
      background-color: var(--ads-search-input-background-color-disabled, $ads-search-input-background-color-disabled);
      border-color: var(--ads-search-border-color-disabled, $ads-search-border-color-disabled);
      color: var(--ads-search-input-color-disabled, $ads-search-input-color-disabled);
    }

    .c-search__icon {
      fill: var(--ads-search-input-color-disabled, $ads-search-input-color-disabled);
    }
  }

  &.has-button {
    border-radius: 10px;
  }

  &__bar {
    align-items: center;
    display: flex;
    flex-grow: 1;
    position: relative;
  }

  &.has-button &__bar {
    background-color: transparent;
    border: 0;
    color: var(--ads-search-button-color, $ads-search-button-color);
  }

  &__icon {
    align-self: center;
    color: var(--ads-search-input-color, $ads-search-input-color);
    display: inline-flex;
    fill: var(--ads-search-input-color, $ads-search-input-color);
    height: 20px;
    margin-left: 1rem;
    margin-right: 1rem;
    margin-top: -8px;
    position: absolute;
    top: 50%;
    transition: background-position 0.125s ease-in-out, margin 0.125s ease-in-out, width 0.125s ease-in-out;
    width: 20px;
    z-index: 2;
  }

  &__input {
    appearance: none;
    -moz-appearance: none;
    -webkit-appearance: none;
    background-color: transparent;
    border: 1px solid var(--ads-search-border-color, $ads-search-border-color);
    border-radius: 0;
    color: var(--ads-search-input-color, $ads-search-input-color);
    flex-grow: 1;
    font-family: inherit;
    font-size: var(--ads-search-font-size, $ads-search-font-size);
    height: var(--ads-search-height, $ads-search-height);
    margin: 0;
    outline: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
    z-index: $layer-1;

    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button {
      display: none;
    }

    &.is-disabled {
      background-color: transparent;
      cursor: not-allowed;
    }
  }

  &.has-button &__input {
    background-color: var(--ads-search-input-background-color, $ads-search-input-background-color);
    border-radius: var(--ads-search-input-border-radius, $ads-search-input-border-radius-ltr);
    color: var(--ads-search-input-color, $ads-search-input-color);
    padding: var(--ads-search-input-has-button-padding, $ads-search-input-has-button-padding-ltr);

    &.is-disabled {
      cursor: not-allowed;
    }
  }

  &.has-button.hide-clear-button &__input {
    padding: var(--ads-search-input-hide-clear-padding-focus, $ads-search-input-hide-clear-padding-focus-ltr);
  }

  &:not(.has-button) &__input {
    border: 0;
    border-bottom: 1px solid var(--ads-search-border-bottom-color, $ads-search-border-bottom-color);
    padding: var(--ads-search-input-padding, $ads-search-input-padding-ltr);

    &:focus {
      padding: var(--ads-search-input-padding-focus, $ads-search-input-padding-focus-ltr);

      & ~ .c-search__icon {
        left: -45px;
      }
    }
  }

  &.hide-clear-button:not(.has-button) &__input {
    padding: var(--ads-search-input-hide-clear-padding, $ads-search-input-hide-clear-padding-ltr);

    &:focus {
      padding: var(--ads-search-input-hide-clear-padding-focus, $ads-search-input-hide-clear-padding-focus-ltr);
    }
  }

  &__clear {
    align-items: center;
    background-color: transparent;
    border: 0;
    cursor: pointer;
    display: flex;
    fill: var(--ads-search-input-clear-color, $ads-search-input-clear-color);
    height: 30px;
    padding: 0 5px;
    position: absolute;
    right: var(--ads-search-input-clear-position-right, $ads-search-input-clear-position-right-ltr);
    left: var(--ads-search-input-clear-position-left, $ads-search-input-clear-position-left-ltr);
    z-index: $layer-1;

    &:focus,
    &:hover {
      fill: var(--ads-search-input-clear-color-hover, $ads-search-input-clear-color-hover) !important;
    }

    svg {
      fill: inherit;
      height: 20px;
      width: 20px;
    }
  }

  &__button {
    flex-shrink: 0;
    overflow: hidden;

    button {
      align-items: center;
      background-color: var(--ads-search-button-background-color, $ads-search-button-background-color);
      border-color: var(--ads-search-border-color, $ads-search-border-color);
      border-radius: var(--ads-search-button-border-radius, $ads-search-button-border-radius-ltr);
      border-style: solid;
      border-width: var(--ads-search-button-border, $ads-search-button-border-ltr);
      color: var(--ads-search-button-color, $ads-search-button-color);
      cursor: pointer;
      display: flex;
      fill: var(--ads-search-button-color, $ads-search-button-color);
      font-size: var(--ads-search-font-size, $ads-search-font-size);
      font-weight: 400;
      height: 100%;
      line-height: 1;
      margin: 0;
      outline: none;
      padding: 0 1rem;

      &:not(.is-disabled):hover {
        background-color: var(--ads-search-button-background-color-hover, $ads-search-button-background-color-hover);
        border-color: var(--ads-search-border-color-hover, $ads-search-border-color-hover);
        color: var(--ads-search-button-color-hover, $ads-search-button-color-hover) !important;
        fill: var(--ads-search-button-color-hover, $ads-search-button-color-hover) !important;
      }

      &.is-disabled {
        border-left: 1px solid var(--ads-search-border-color, $ads-search-border-color);
        color: var(--ads-search-input-color-disabled-dark, $ads-search-input-color-disabled-dark);
        cursor: not-allowed;
      }

      svg {
        fill: inherit;
        height: 20px;
        width: 20px;
      }
    }
  }

  &__suggestions-alert {
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
  }

  &__suggestions {
    @include font-styles;
    background-color: var(--ads-search-suggestions-background-color, $ads-search-suggestions-background-color);
    border: 1px solid var(--ads-search-border-color-focus, $ads-search-border-color-focus);
    border-top-width: 0;
    left: 0;
    overflow: hidden;
    position: absolute;
    right: 0;
    top: var(--ads-search-height, $ads-search-height);
    z-index: $layer-3;

    &.is-hidden {
      display: none !important;
    }

    &__suggestion {
      border-bottom: 1px solid var(--ads-search-suggestions-border-color, $ads-search-suggestions-border-color);
      cursor: pointer;
      display: block;
      min-height: 35px;
      padding: 0.5rem 1rem;
      text-decoration: none;
      width: 100%;

      .link {
        @include ads-anchor;
        color: var(--ads-search-suggestions-link-color, $ads-search-suggestions-link-color);
        font-family: inherit;
      }

      &:hover,
      &.is-active {
        background-color: var(--ads-search-suggestions-background-color-active, $ads-search-suggestions-background-color-active);
      }

      .description {
        color: var(--ads-search-suggestions-color, $ads-search-suggestions-color);
        font-size: var(--ads-search-font-size, $ads-search-font-size);
        margin: 3px;
      }
    }
  }

  &.has-button .c-search__suggestions {
    border-radius: 5px 0 5px 5px;
    border-top-width: 1px;
    margin-top: 2px;
  }
}
